import { Typography } from "@mui/material";
import localize from "../../common/localize";
import {
  getDatesArray,
  getOperationalStatusColorById,
  getOperationalStatusNameFromId,
  getValueInCurrency,
} from "../../common/utils";
import { getCurrencyFromSetup } from "../../common/EntitiesManagement";
import { Height } from "@mui/icons-material";

const generateOperationalStatusChartOptions = (data) => {
  const colors = [];
  const series = [];
  const labels = [];
  data.forEach((operationalStatusObj) => {
    const label = getOperationalStatusNameFromId(
      operationalStatusObj.operationalStatusId
    );
    const serie = operationalStatusObj?.dataCount ?? 0;
    const color = getOperationalStatusColorById(
      operationalStatusObj.operationalStatusId
    );
    series.push(serie);
    colors.push(color);
    labels.push(label);
  });

  return {
    series,
    colors,
    labels,
    title: localize.operational_status_analytics,
  };
};

const generateStatusChartOptions = (data) => {
  const series = [];
  const colors = [];
  const labels = [];
  const legend = {
    position: "right",
  };
  const plotOptions = {
    pie: {
      donut: {
        size: "65%",
        labels: {
          show: true,
          name: {
            show: true,
            fontSize: "18px",
            offsetY: -10, // 🔹 sube el label un poco
          },
          value: {
            show: true,
            fontSize: "16px",
            offsetY: 10,
          },
          total: {
            show: false,
          },
        },
      },
    },
  };

  data.forEach((option) => {
    const label = option?.statusName ?? localize.without_status;
    const serie = option?.dataCount ?? 0;
    const color = option?.backColor?.toUpperCase() ?? "#000000";
    series.push(serie);
    colors.push(color);
    labels.push(label);
  });

  return { series, colors, labels, legend, plotOptions };
};

const generatePersonalChartOptions = (data) => {
  const labels = [];
  const series = [];
  data.forEach((option) => {
    const label = option?.fullName ?? localize.not_assigned;
    const serie = option?.dataCount ?? 0;
    labels.push(label);
    series.push(serie);
  });

  return { series, labels };
};

const generateEventsByTypeChartOptions = (data) => {
  const labels = [];
  const series = [];
  data.forEach((option) => {
    const label = option?.typeName ?? localize.not_assigned;
    const serie = option?.dataCount ?? 0;
    labels.push(label);
    series.push(serie);
  });

  return { series, labels };
};

const generateEventsByProceedingChartOptions = (data) => {
  const labels = [];
  const series = [];
  data.forEach((option) => {
    const label = option?.proceedingName ?? localize.not_assigned;
    const serie = option?.dataCount ?? 0;
    labels.push(label);
    series.push(serie);
  });

  return { series, labels };
};

const generateLocationChartOptions = (data) => {
  const labels = [];
  const series = [];
  const legend = {
    position: "right",
  };

  data.forEach((option) => {
    const serie = option?.dataCount ?? 0;
    const label = option?.locationName ?? localize.without_name;
    series.push(serie);
    labels.push(label);
  });

  return { series, labels, legend };
};

const generateClientsChartOptions = (data) => {
  const labels = [];
  const series = [];
  data.forEach((option) => {
    const label = option?.clientName ?? localize.without_name;
    const serie = option?.dataCount ?? 0;
    labels.push(label);
    series.push(serie);
  });

  return { series, labels, title: localize.events_by_clients_analytics };
};

const generatePaymentResumeChartOptions = (data, initialDate, finalDate) => {
  const labels = [];
  const series = [
    {
      name: localize.service_value,
      data: [],
      color: "#0080FF",
    },
    {
      name: localize.earning_spare_parts_value,
      data: [],
      color: "#FFFF33",
    },
    {
      name: localize.additional_costs,
      data: [],
      color: "#8A8686",
    },
    /*{
      name: localize.total_event,
      data: [],
      color: "#8e2ece",
    },*/
    {
      name: localize.payment_to_technician,
      data: [],
      color: "#DB0B27",
    },
    {
      name: localize.real_income,
      data: [],
      color: "#00CC66",
    },
  ];

  const datesArray = getDatesArray(initialDate, finalDate);

  datesArray.forEach((dateValue) => {
    labels.push(dateValue);
    if (data && data.length > 0) {
      const foundDate = data.find((x) => x.paymentDay === dateValue);

      if (foundDate) {
        series[0].data.push(foundDate.executionTotalValue); // service_value
        // earning_spare_parts_value
        series[1].data.push(
          foundDate.sparePartsTotalValue - foundDate.sparePartsRealTotalValue
        );
        series[2].data.push(foundDate.additionalCostsTotalValue);
        // series[3].data.push(foundDate.total);
        series[3].data.push(foundDate.paymentToTechnician < 0 ? 0 : foundDate.paymentToTechnician);
        series[4].data.push(
          foundDate.total -
            foundDate.sparePartsRealTotalValue -
            foundDate.paymentToTechnician
        );
      } else {
        series[0].data.push(0);
        series[1].data.push(0);
        series[2].data.push(0);
        series[3].data.push(0);
        series[4].data.push(0);
        // series[5].data.push(0);
      }
    } else {
      series[0].data.push(0);
      series[1].data.push(0);
      series[2].data.push(0);
      series[3].data.push(0);
      series[4].data.push(0);
      // series[5].data.push(0);
    }
  });
  const title = {
    text: `${localize.incomes} ( ${getValueInCurrency(
      getCurrencyFromSetup(),
      series[series.length - 1].data.reduce(
        (partialSum, a) => partialSum + a,
        0
      )
    )})`,
    margin: 30,
    offSetY: 0,
    align: "center",
    floating: "false",
    style: {
      fontSize: "0.8em",
      fontWeight: 500,
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    },
  };

  return { series, labels, title };
};

const generateOptions = (data, type, initialDate, finalDate) => {
  switch (type) {
    case "operationalStatus":
      return generateOperationalStatusChartOptions(data);
    case "status":
      return generateStatusChartOptions(data);
    case "personal":
      return generatePersonalChartOptions(data);
    case "type":
      return generateEventsByTypeChartOptions(data);
    case "proceeding":
      return generateEventsByProceedingChartOptions(data);
    case "state":
    case "city":
      return generateLocationChartOptions(data);
    case "clients":
      return generateClientsChartOptions(data);
    case "paymentResume":
      return generatePaymentResumeChartOptions(data, initialDate, finalDate);
    default:
      break;
  }
};

export const getDonutChartOptions = ({ data, type, chartTitle }) => {
  const options = generateOptions(data, type);
  return {
    options: {
      chart: {
        id: "donutChart",
        type: "donut",
      },
      legend: options.legend,
      responsive: [
        {
          breakpoint: 200,
          options: {
            chart: {
              width: 300,
              Height: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      labels: options.labels,
      colors: options.colors,
      title: {
        text: chartTitle ?? options.title,
        align: "center",
      },
    },
    series: options.series,
    plotOptions: options.plotOptions,
  };
};

export const getLineChartoptions = ({
  data,
  initialDate,
  finalDate,
  isMobile,
}) => {
  const options = generateOptions(
    data,
    "paymentResume",
    initialDate,
    finalDate
  );

  return {
    options: {
      yaxis: {
        labels: {
          formatter: (value) =>
            getValueInCurrency(getCurrencyFromSetup(), value),
        },
      },
      tooltip: {
        y: {
          formatter: (value) =>
            getValueInCurrency(getCurrencyFromSetup(), value),
        },
      },
      chart: {
        id: "lineChart",
        type: "line",
        toolbar: {
          show: !isMobile,
        },
      },
      labels: options.labels,
      title: options.title,
    },
    series: options.series,
  };
};

export const getLineChartJsOptions = ({
  data,
  initialDate,
  finalDate,
  isMobile,
}) => {
  const raw = generatePaymentResumeChartOptions(data, initialDate, finalDate);

  const chartData = {
    labels: raw.labels,
    datasets: raw.series.map((s) => ({
      label: s.name,
      data: s.data,
      borderColor: s.color,
      backgroundColor: s.color + "20",
      tension: 0.3,
      pointRadius: 3,
      pointHoverRadius: 6,
    })),
  };

  const currencyFormatter = (value) =>
    getValueInCurrency(getCurrencyFromSetup(), value);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: raw.title.text,
        font: {
          size: 13,
          weight: 500,
          family: raw.title.style.fontFamily,
        },
      },
      legend: {
        display: !isMobile,
        position: "bottom",
        labels: { boxWidth: 12, padding: 12 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${currencyFormatter(ctx.raw)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => currencyFormatter(value),
        },
      },
    },
  };

  return { chartData, chartOptions };
};

export const getBarChartOptions = ({ data, type, horizontalBar }) => {
  const options = generateOptions(data, type);
  return {
    options: {
      chart: {
        id: "barChart",
        type: "bar",
      },
      plotOptions: {
        bar: {
          barHeight: "100%",
          distributed: true,
          dataLabels: {
            position: "bottom",
          },
          borderRadius: 4,
          horizontal: horizontalBar,
        },
      },
      colors: options?.colors,
      xaxis: {
        categories: options.labels,
      },
      yaxis: {
        labels: { show: false },
      },
      title: {
        text: options.title,
        align: "center",
        floating: true,
      },
      tooltip: {
        theme: "dark",
        x: {
          show: false,
        },
        y: {
          title: {
            formatter(val, opts) {
              const seriesIndex = opts.dataPointIndex;
              return `${opts.w.config.xaxis.categories[seriesIndex]}`;
            },
          },
        },
      },
    },
    series: [{ data: options.series }],
  };
};

export const getDonutChartJsOptions = ({ data, type, chartTitle }) => {
  const options = generateOptions(data, type);

  const defaultColors = [
    "#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0",
    "#546E7A", "#26a69a", "#D10CE8", "#2E93fA", "#66DA26",
  ];

  const colors = options.colors?.length
    ? options.colors
    : options.labels.map((_, i) => defaultColors[i % defaultColors.length]);

  const chartData = {
    labels: options.labels || [],
    datasets: [
      {
        data: options.series || [],
        backgroundColor: colors,
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    cutout: "65%",
    plugins: {
      title: {
        display: !!(chartTitle || options.title),
        text: chartTitle || options.title || "",
        font: { size: 13, weight: 500 },
      },
      legend: {
        display: true,
        position: options.legend?.position || "right",
        labels: { boxWidth: 12, padding: 10 },
      },
    },
  };

  return { chartData, chartOptions };
};

export const getBarChartJsOptions = ({ data, type, horizontalBar }) => {
  const options = generateOptions(data, type);

  const defaultColors = [
    "#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0",
    "#546E7A", "#26a69a", "#D10CE8", "#2E93fA", "#66DA26",
  ];

  const colors = options.colors?.length
    ? options.colors
    : options.labels.map((_, i) => defaultColors[i % defaultColors.length]);

  const chartData = {
    labels: options.labels || [],
    datasets: [
      {
        data: options.series || [],
        backgroundColor: colors,
        borderRadius: 4,
        barPercentage: 0.8,
      },
    ],
  };

  const chartOptions = {
    indexAxis: horizontalBar ? "y" : "x",
    plugins: {
      title: {
        display: !!options.title,
        text: options.title || "",
        font: { size: 13, weight: 500 },
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => tooltipItems[0]?.label || "",
          label: (ctx) => ` ${ctx.raw}`,
        },
      },
    },
    scales: {
      ...(horizontalBar
        ? { x: { display: false }, y: { ticks: { font: { size: 11 } } } }
        : { y: { display: false }, x: { ticks: { font: { size: 11 } } } }),
    },
  };

  return { chartData, chartOptions };
};
